<template>
  <sui-modal size="small" v-model="visible" :closable="false" :style="{zIndex}">
    <sui-modal-header class="header">
      Add hosts
    </sui-modal-header>
    <sui-modal-content>
      <sui-form warning :error="hasApiErrors" :loading="loading" @submit.prevent="submit()" v-if="entity">
        <sui-tab ref="tab" :menu="{color: 'blue', attached: false, tabular: false, pointing: true, widths: 2}" :activeIndex="tab" @change="onChangeTab">
          <sui-tab-pane class="secondary" title="Create cloud host" icon="cloud" :disabled="!access.hostCreate" :attached="false">
            <sui-message warning icon="warning sign" v-if="isRestricted">
              <router-link class="ui green right floated button" to="/settings/plans" @click.native="close()" data-goal="CREATE_HOST_SUBSCRIBE">Change plan</router-link>
              Creating cloud hosts<br>is not available on your tariff plan!<br>
            </sui-message>
            <FormHost v-model="entity" mode="creating" />
          </sui-tab-pane>
          <sui-tab-pane class="secondary" title="Connect own host" icon="server" :disabled="!access.hostCreate" :attached="false">
            <sui-message warning icon="warning sign" v-if="isRestricted">
              <router-link class="ui green right floated button" to="/settings/plans" @click.native="close()" data-goal="CONNECT_HOST_SUBSCRIBE" >Change plan</router-link>
              Connecting own hosts<br>is not available on your tariff plan!<br>
            </sui-message>
            <FormHost v-model="entity" mode="creatingCustom" />
            <br>
            <Logo class="provider-logo" :entity="entity" />
            <p>
              You can connect own hosts to D2C and use them as usual cloud hosts with some limitations.
              After confirmation you will have to run a setup script installing our software and establishing the connection.
            </p>
            <sui-accordion>
              <sui-accordion-title><i class="dropdown icon" />Host requirements</sui-accordion-title>
              <sui-accordion-content>
                <ul>
                  <li>Ubuntu Server 18.04 or 20.04, Debian 9 or 10</li>
                  <li>Linux kernel 4.2 or later</li>
                  <li>RAM: 0.5GB miminum, 1GB or higher recommended</li>
                  <li>Free disk space: 5GB</li>
                  <li>Opened 22 port for SSH</li>
                  <li>Opened 6783 and 6784 ports for Weave Network (TCP/UDP)</li>
                </ul>
                <p>
                  <small>
                    For better performance we recommend to ensure that VXLAN tunneling is allowed.<br>
                    We don't recommend Linux kernel 4.12 and 4.13 because of their poor performance of VXLAN tunneling (60x decrease)
                  </small>
                </p>
              </sui-accordion-content>
              <sui-accordion-title><i class="dropdown icon" />Installed software</sui-accordion-title>
              <sui-accordion-content>
                <ul>
                  <li>Docker</li>
                  <li>Weave Network</li>
                  <li>Telegraf</li>
                  <li>D2C HealthCheck Daemon</li>
                </ul>
              </sui-accordion-content>
            </sui-accordion>
          </sui-tab-pane>
        </sui-tab>
        <sui-message error v-if="apiErrors">
          {{apiErrors}}
        </sui-message>
      </sui-form>
    </sui-modal-content>
    <sui-modal-actions>
      <sui-button-group>
        <sui-button icon="check" label-position="left" color="green" :disabled="isRestricted || loading || hasEntityErrors" @click="submit()">Confirm</sui-button>
        <sui-button icon="ban" label-position="right" color="red" @click="cancel()">Cancel</sui-button>
      </sui-button-group>
    </sui-modal-actions>
    <sui-dimmer class="dimmer" :active="success" inverted>
      <h2 class="ui icon header">
        <i class="green check circle icon" />
        {{successMessage}}
      </h2>
    </sui-dimmer>
  </sui-modal>
</template>

<script>
  import {mapState, mapGetters} from 'vuex'
  import utils from '@/utils'
  import FormHost from '@/components/forms/Host'
  import ModalForm from '@/mixins/ModalForm'

  export default {
    name: 'ModalCreateHost',
    mixins: [
      ModalForm,
    ],
    components: {
      FormHost,
    },
    data() {
      return {
        tab: 0,
      }
    },
    computed: {
      ...mapState(['lastProviderId']),
      ...mapGetters(['access', 'account', 'hosts', 'keys', 'providersById', 'providersByPurpose']),
      mode() {
        switch (this.tab) {
          case 0: return 'creating'
          case 1: return 'creatingCustom'
          case 2: return 'creatingDemo'
        }
        return 'creating'
      },
      successMessage() {
        switch (this.tab) {
          case 0: return 'Deploying started'
          case 1: return 'Preparing started'
          case 2: return 'Deploying started'
        }
        return 'Success'
      },
      isRestricted() {
        return this.account.limits && !this.account.limits.hosts && (this.mode !== 'creatingDemo')
      },
      demoHosts() {
        return this.hosts.filter(host => host.demo)
      },
      maxDemoAmount() {
        return this.entity.constructor.fields && this.entity.constructor.fields.amt ? this.entity.constructor.fields.amt.creatingDemo.max : 0
      },
      maxDemoHours() {
        return this.entity.constructor.fields && this.entity.constructor.fields.hours ? this.entity.constructor.fields.hours.max : 0
      },
      availableDemo() {
        return this.maxDemoAmount - this.demoHosts.length
      },
    },
    methods: {
      open(entity = {}, tab = 0, callback) {
        this.visible = true
        this.$options.callback = callback
        this.tab = tab
        this.createEntity(entity)
      },
      reset() {
        this.apiErrors = null
        this.success = false
        this.entity = null
        this.tab = 0
      },
      createEntity(entity = {}) {
        const name = (!entity.name || entity.name.includes('demo-')) ? utils.generateName() : entity.name
        const provider = this.providersById[this.lastProviderId] || this.providersByPurpose.hosting[0]
        switch (this.tab) {
          case 0:
            this.entity = {
              provider: provider ? provider.id : null,
              name,
              cloud: true,
              keys: entity.keys || this.keys.filter(key => key.default).map(key => key.id),
              labels: entity.labels || [],
            }
            this.selectName()
            break;
          case 1:
            this.entity = {
              providerType: 'custom',
              labels: entity.labels || [],
              name,
            }
            this.selectName()
            break;
          case 2:
            this.entity = {
              name: `demo-${this.account.shortId}`,
              instance: '1xCPU-2GB',
              region: 'nl-ams1',
              amt: 1,
              hours: 1,
              providerType: 'Upcloud',
              demo: true,
            }
            break;
        }
      },
      selectName() {
        this.$nextTick(() => {
          const nameInput = this.$el.querySelector('.input input')
          if (nameInput) {
            nameInput.select()
          }
        })
      },
      onChangeTab(event, vm, index) {
        this.apiErrors = null
        this.tab = index
        this.createEntity(this.entity)
      },
      onSuccess(result) {
        this.close()
        const GOALS = ['CREATE_HOST', 'CONNECT_HOST', 'CREATE_DEMOHOST']
        utils.reachGoal(`${GOALS[this.tab]}_SUCCESS`)
        if (this.$options.callback) {
          this.$options.callback(result)
        }
      },
      onFail() {
        const GOALS = ['CREATE_HOST', 'CONNECT_HOST', 'CREATE_DEMOHOST']
        utils.reachGoal(`${GOALS[this.tab]}_FAIL`)
      },
    },
    created() {
      this.$modals.createHost = this
    },
  }
</script>

<style scoped>
  ul { margin: 0 0 .5em!important }
  p { margin: 0 0 .5em!important; }
  .provider-logo { float: right; height: 4.5em; }
  .accordion .content { padding: 0!important; }
</style>
